import React, { FC, useEffect, useMemo, useRef } from 'react';

import { Box, Button, Flex } from '@chakra-ui/react';
import { Form, useFormikContext } from 'formik';
import { FormControl, FormLabel } from '@chakra-ui/form-control';
import { Select } from 'chakra-react-select';

import FormikDatepicker from 'components/primitives/FormikDatepicker';
import FormikInput from 'components/primitives/FormikInput';
import FormikCheckBox from 'components/primitives/FormikCheckBox';
import FormikTextArea from 'components/primitives/FormikTextArea';

import { CreatePurchaseOrderModalViewProps } from './CreatePurchaseOrderModal.props';
import {
  applyLocationAddressToForm,
  applyManufacturerAddressToForm,
  CreatePurchaseOrderFormValues,
  generatePoNumber,
  getEmptySkuLine,
  getShipToContactDetails,
} from './CreatePurchaseOrderModal.utils';
import { formatManufacturerEmails } from 'utils/manufacturer';
import { getDefaultShipToContactId } from 'api/employees';
import PurchaseOrderSkuLines from './PurchaseOrderSkuLines';
import PurchaseOrderPdfPreview from './PurchaseOrderPdfPreview';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

type FormProps = Pick<
  CreatePurchaseOrderModalViewProps,
  | 'manufacturers'
  | 'washHubs'
  | 'arts'
  | 'washHubAdmins'
  | 'isLoadingManufacturers'
  | 'isLoadingWashHubs'
  | 'isLoadingArts'
  | 'isLoadingWashHubAdmins'
  | 'onDestinationHubChange'
  | 'isEditing'
  | 'formId'
>;

const CreatePurchaseOrderForm: FC<FormProps> = ({
  manufacturers,
  washHubs,
  arts,
  washHubAdmins,
  isLoadingManufacturers,
  isLoadingWashHubs,
  isLoadingArts,
  isLoadingWashHubAdmins,
  onDestinationHubChange,
  isEditing = false,
  formId = 'create-purchase-order-form',
}) => {
  const {
    values,
    setFieldValue,
  } = useFormikContext<CreatePurchaseOrderFormValues>();

  const manufacturerOptions = useMemo(
    () =>
      manufacturers.map((manufacturer) => ({
        label: manufacturer.name,
        value: manufacturer._id,
      })),
    [manufacturers],
  );

  const washHubOptions = useMemo(
    () =>
      washHubs.map((washHub) => ({
        label: washHub.name,
        value: washHub._id,
      })),
    [washHubs],
  );

  const washHubAdminOptions = useMemo(
    () =>
      washHubAdmins.map((admin) => ({
        label: `${admin.fname ?? ''} ${admin.lname ?? ''}`.trim(),
        value: admin._id,
      })),
    [washHubAdmins],
  );

  const selectedManufacturer = manufacturers.find(
    (manufacturer) => manufacturer._id === values.manufacturerId,
  );
  const selectedWashHub = washHubs.find(
    (washHub) => washHub._id === values.destinationHubId,
  );
  const shipToContactDetails = getShipToContactDetails(values, washHubAdmins);
  const shipToContact = shipToContactDetails.name;
  const destinationHubName = values.isManualDestinationHub
    ? values.manualDestinationHubName
    : selectedWashHub?.name ?? '';
  const previousManufacturerIdRef = useRef(values.manufacturerId);

  useEffect(() => {
    if (previousManufacturerIdRef.current === values.manufacturerId) {
      return;
    }

    previousManufacturerIdRef.current = values.manufacturerId;
    setFieldValue('skuLines', [getEmptySkuLine()]);
  }, [values.manufacturerId, setFieldValue]);

  useEffect(() => {
    if (
      values.isManualShipToContact ||
      values.isManualDestinationHub ||
      !values.destinationHubId ||
      isLoadingWashHubAdmins
    ) {
      return;
    }

    const defaultContactId = getDefaultShipToContactId(
      washHubAdmins,
      values.destinationHubId,
    );

    if (!defaultContactId) {
      return;
    }

    const isCurrentContactValid = washHubAdmins.some(
      (admin) => admin._id === values.shipToContactId,
    );

    if (!isCurrentContactValid) {
      setFieldValue('shipToContactId', defaultContactId);
    }
  }, [
    values.destinationHubId,
    values.isManualDestinationHub,
    values.isManualShipToContact,
    values.shipToContactId,
    washHubAdmins,
    isLoadingWashHubAdmins,
    setFieldValue,
  ]);

  useEffect(() => {
    if (isEditing) {
      return;
    }

    const shipToContact = washHubAdmins.find(
      (admin) => admin._id === values.shipToContactId,
    );
    const hubName = values.isManualDestinationHub
      ? values.manualDestinationHubName
      : selectedWashHub?.name ?? '';

    if (!values.date || !hubName) {
      return;
    }

    setFieldValue(
      'poNumber',
      generatePoNumber({
        date: values.date,
        hubName,
        userFirstName: shipToContact?.fname ?? '',
        userLastName: shipToContact?.lname ?? '',
        manufacturerName: selectedManufacturer?.name ?? '',
      }),
    );
  }, [
    isEditing,
    values.date,
    values.isManualDestinationHub,
    values.manualDestinationHubName,
    values.destinationHubId,
    selectedWashHub?.name,
    selectedManufacturer?.name,
    values.shipToContactId,
    washHubAdmins,
    setFieldValue,
  ]);

  return (
    <Form
      id={formId}
      style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Flex gap={6} align="stretch" flex="1" minH={0} overflow="hidden">
        <Flex
          direction="column"
          flex={1}
          gap={4}
          minH={0}
          overflowY="auto"
          pr={2}>
          <FormikDatepicker
            name="date"
            label="Date"
            labelProps={labelProps}
            isRequired
          />

          <FormControl isRequired>
            <FormLabel {...labelProps}>Manufacturer</FormLabel>
            <Select
              placeholder="Select manufacturer"
              isLoading={isLoadingManufacturers}
              options={manufacturerOptions}
              value={
                manufacturerOptions.find(
                  (option) => option.value === values.manufacturerId,
                ) ?? null
              }
              onChange={(option) => {
                const manufacturer = manufacturers.find(
                  (item) => item._id === option?.value,
                );

                setFieldValue('manufacturerId', option?.value ?? '');
                const addressValues = applyManufacturerAddressToForm(
                  manufacturer?.address,
                );
                Object.entries(addressValues).forEach(([key, value]) => {
                  setFieldValue(key, value);
                });
                setFieldValue(
                  'manufacturerContact',
                  formatManufacturerEmails(manufacturer?.email),
                );
              }}
            />
          </FormControl>

          <FormikInput
            name="manufacturerAddress"
            label="Manufacturer Address"
            labelProps={labelProps}
            isReadOnly
          />

          <FormikInput
            name="manufacturerContact"
            label="Manufacturer Contact"
            labelProps={labelProps}
          />

          <Flex align="flex-end" gap={3}>
            <Box flex={1}>
              {values.isManualDestinationHub ? (
                <FormikInput
                  name="manualDestinationHubName"
                  label="Destination Hub"
                  labelProps={labelProps}
                  isRequired
                />
              ) : (
                <FormControl isRequired>
                  <FormLabel {...labelProps}>Destination Hub</FormLabel>
                  <Select
                    placeholder="Select destination hub"
                    isLoading={isLoadingWashHubs}
                    options={washHubOptions}
                    value={
                      washHubOptions.find(
                        (option) => option.value === values.destinationHubId,
                      ) ?? null
                    }
                    onChange={(option) => {
                      const washHub = washHubs.find(
                        (item) => item._id === option?.value,
                      );

                      setFieldValue('destinationHubId', option?.value ?? '');
                      setFieldValue('shipToContactId', '');
                      setFieldValue('manualShipToContact', '');
                      onDestinationHubChange(option?.value ?? '');

                      const addressValues = applyLocationAddressToForm(washHub);
                      Object.entries(addressValues).forEach(([key, value]) => {
                        setFieldValue(key, value);
                      });
                    }}
                  />
                </FormControl>
              )}
            </Box>
            <Button
              mb={1}
              minW="130px"
              variant="outline"
              onClick={() => {
                const nextValue = !values.isManualDestinationHub;
                setFieldValue('isManualDestinationHub', nextValue);
                setFieldValue('destinationHubId', '');
                setFieldValue('manualDestinationHubName', '');
                setFieldValue('shipToContactId', '');
                setFieldValue('manualShipToContact', '');
                onDestinationHubChange('');

                if (nextValue) {
                  setFieldValue('shipToAddressLine1', '');
                  setFieldValue('shipToAddressLine2', '');
                  setFieldValue('shipToCity', '');
                  setFieldValue('shipToState', '');
                  setFieldValue('shipToZip', '');
                  setFieldValue('liftGateRequired', false);
                  setFieldValue('shippingNotes', '');
                }
              }}>
              {values.isManualDestinationHub
                ? 'Use Dropdown'
                : 'Enter Manually'}
            </Button>
          </Flex>

          <FormikInput
            name="shipToAddressLine1"
            label="Ship To Address Line 1"
            labelProps={labelProps}
            isReadOnly={!values.isManualDestinationHub}
            isRequired
          />
          <FormikInput
            name="shipToAddressLine2"
            label="Ship To Address Line 2"
            labelProps={labelProps}
            isReadOnly={!values.isManualDestinationHub}
          />
          <Flex gap={3}>
            <FormikInput
              name="shipToCity"
              label="City"
              labelProps={labelProps}
              isReadOnly={!values.isManualDestinationHub}
              isRequired
            />
            <FormikInput
              name="shipToState"
              label="State"
              labelProps={labelProps}
              isReadOnly={!values.isManualDestinationHub}
              isRequired
            />
            <FormikInput
              name="shipToZip"
              label="Zip"
              labelProps={labelProps}
              isReadOnly={!values.isManualDestinationHub}
              isRequired
            />
          </Flex>

          <Flex align="flex-end" gap={3}>
            <Box flex={1}>
              {values.isManualShipToContact ? (
                <FormikInput
                  name="manualShipToContact"
                  label="Ship To Contact"
                  labelProps={labelProps}
                  isRequired
                />
              ) : (
                <FormControl isRequired>
                  <FormLabel {...labelProps}>Ship To Contact</FormLabel>
                  <Select
                    placeholder="Select contact"
                    isLoading={isLoadingWashHubAdmins}
                    isDisabled={!values.destinationHubId}
                    options={washHubAdminOptions}
                    value={
                      washHubAdminOptions.find(
                        (option) => option.value === values.shipToContactId,
                      ) ?? null
                    }
                    onChange={(option) => {
                      setFieldValue('shipToContactId', option?.value ?? '');
                    }}
                  />
                </FormControl>
              )}
            </Box>
            <Button
              mb={1}
              minW="130px"
              variant="outline"
              isDisabled={
                values.isManualDestinationHub || !values.destinationHubId
              }
              onClick={() => {
                setFieldValue(
                  'isManualShipToContact',
                  !values.isManualShipToContact,
                );
                setFieldValue('shipToContactId', '');
                setFieldValue('manualShipToContact', '');
              }}>
              {values.isManualShipToContact ? 'Use Dropdown' : 'Enter Manually'}
            </Button>
          </Flex>

          <FormikInput name="poNumber" label="PO#" labelProps={labelProps} />

          <FormikCheckBox
            field="liftGateRequired"
            name="Liftgate is required"
            isDisabled={
              values.isManualDestinationHub || !values.destinationHubId
            }
          />

          <FormikTextArea
            name="shippingNotes"
            label="Shipping Notes"
            labelProps={labelProps}
            isReadOnly={
              values.isManualDestinationHub || !values.destinationHubId
            }
          />

          <PurchaseOrderSkuLines
            manufacturerId={values.manufacturerId}
            arts={arts}
            isLoadingArts={isLoadingArts}
          />

          <Flex gap={3}>
            <FormikInput
              name="shippingCost"
              type="number"
              label="Shipping Cost"
              labelProps={labelProps}
            />
            <FormikInput
              name="tax"
              type="number"
              label="Tax"
              labelProps={labelProps}
            />
            <FormikInput
              name="costAdjustment"
              type="number"
              label="Cost Adjustment"
              labelProps={labelProps}
            />
          </Flex>
        </Flex>

        <Box
          flex={1}
          minH={0}
          overflow="hidden"
          display="flex"
          flexDirection="column">
          <PurchaseOrderPdfPreview
            values={values}
            manufacturerName={selectedManufacturer?.name ?? ''}
            destinationHubName={destinationHubName}
            shipToContact={shipToContact}
            shipToContactPhone={shipToContactDetails.phone}
            shipToContactEmail={shipToContactDetails.email}
          />
        </Box>
      </Flex>
    </Form>
  );
};

export default CreatePurchaseOrderForm;
